import {createApp, h, provide} from 'vue';
import {ApolloClients} from '@vue/apollo-composable';
import {apolloClient} from "./graphql/client";
import {filters} from "@/vue/filters";

// Vuetify
import 'vuetify/styles'
import {createVuetify} from 'vuetify'
import { aliases, mdi } from 'vuetify/iconsets/mdi-svg'
import {md1} from 'vuetify/blueprints'
// Translations provided by Vuetify
import {cs} from 'vuetify/locale'
// Lab components
import {VTimePicker} from 'vuetify/labs/VTimePicker'
import {setupSentry} from "@/sentry";
import {router} from "@/router";


export function createComponentApp(rootComponent, props) {
    const app = createApp({
        setup() {
            provide(ApolloClients, {
                default: apolloClient,
            });
        },

        render() {
            return h(rootComponent, props);
        },
    });
    setupVueApp(app);
    return app;
}

/**
 *
 * @param app
 */
export function setupVueApp(app) {
    setupSentry(app);

    app.use(router);

    const globalProps = {
        ...filters,
    };

    const extraComponents = {
        [VTimePicker.name]: VTimePicker,
    }

    // Vuetify
    const vuetify = createVuetify({
        components: extraComponents,
        blueprint: md1,
        // Single source of truth for all app colors. Values come from Vuetify's
        // stock Material Design palette. The brand-named slots (blue/green/red/…)
        // mirror the old colors.scss variable names so SCSS can reference them as
        // `rgb(var(--v-theme-<name>))`. Vuetify emits these on :root, so they also
        // work in the server-rendered Vue islands that mount outside <v-app>.
        //
        // IMPORTANT: Vuetify only emits them on pages where an island actually
        // mounts. Django pages with no island (login, password reset, profile,
        // hpo-edu, …) get the same palette from the static `:where(:root)` block
        // in `e_web/static/src/css/_theme-vars.scss`. Keep the two in sync — any
        // color changed here must be changed there too.
        theme: {
            defaultTheme: 'light',
            themes: {
                light: {
                    dark: false,
                    colors: {
                        // Semantic colors used by Vuetify's own components.
                        primary: '#1976D2', // blue darken-2
                        secondary: '#546E7A', // blue-grey darken-1
                        error: '#B00020', // Vuetify default error (strong, high-contrast)
                        info: '#2196F3', // blue base
                        success: '#4CAF50', // green base
                        warning: '#FB8C00', // orange darken-1

                        // Brand-name slots remapped to the Material palette.
                        white: '#FFFFFF', // shades white
                        'white-hover': '#FAFAFA', // grey lighten-5
                        'blue-light': '#BBDEFB', // blue lighten-4
                        blue: '#1976D2', // blue darken-2
                        'blue-hover': '#0D47A1', // blue darken-4
                        'dark-green': '#1B5E20', // green darken-4
                        'green-light': '#E8F5E9', // green lighten-5
                        green: '#4CAF50', // green base
                        'green-hover': '#388E3C', // green darken-2
                        'red-light': '#FFCDD2', // red lighten-4
                        red: '#F44336', // red base
                        'medium-red': '#E53935', // red darken-1
                        'red-hover': '#B71C1C', // red darken-4
                        'light-red': '#FFEBEE', // red lighten-5
                        'light-gray': '#F5F5F5', // grey lighten-4
                        'light-gray-bg': '#EEEEEE', // grey lighten-3
                        gray: '#424242', // grey darken-3
                        'gray-shade': '#BDBDBD', // grey lighten-1
                        'dark-gray': '#212121', // grey darken-4
                        black: '#000000', // shades black
                        yellow: '#FFEB3B', // yellow base
                        orange: '#FFA726', // orange lighten-1
                        'orange-hover': '#FB8C00', // orange darken-1
                        spindle: '#B0BEC5', // blue-grey lighten-3
                        border: '#CFD8DC', // blue-grey lighten-4
                        highlight: '#E1F5FE', // light-blue lighten-5
                        'light-blue-bg': '#B3E5FC', // light-blue lighten-4
                        'overlay-color': '#263238', // blue-grey darken-4 (used at .75 opacity)
                        link: '#1976D2', // = blue
                        text: '#212121', // = dark-gray
                        'light-text': '#F5F5F5', // = light-gray
                    },
                },
            },
        },
        defaults: {
            VTextField: {variant: 'outlined'},
            VTextarea: {variant: 'outlined'},
            VSelect: {variant: 'outlined'},
            VAutocomplete: {variant: 'outlined'},
            VCombobox: {variant: 'outlined'},
            VFileInput: {variant: 'outlined'},
            VNumberInput: {variant: 'outlined'},
        },
        locale: {
            locale: 'cs',
            messages: {cs},
        },
        icons: {
            defaultSet: 'mdi',
            aliases,
            sets: {
                mdi,
            },
        },
    })
    app.use(vuetify)


    if (app.config.globalProperties) {
        Object.assign(app.config.globalProperties, globalProps);
    } else {
        app.config.globalProperties = globalProps;
    }
}
